import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useSiteContent } from "@/context/SiteContentContext";
import { Button } from "@/components/ui/button";
import { Menu, X, Phone, Mail, MapPin } from "lucide-react";
import AnnouncementPopup from "@/components/AnnouncementPopup";
import LayersHero from "@/components/LayersHero";

const NAV_ROUTES = [
  { to: "/", key: "home", end: true },
  { to: "/servizi", key: "servizi" },
  { to: "/chi-siamo", key: "chisiamo" },
  { to: "/contatti", key: "contatti" },
];
const NAV_FALLBACK = { home: "Home", servizi: "Servizi", chisiamo: "Chi siamo", contatti: "Contatti" };

export default function PublicLayout({ children }) {
  const { user } = useAuth();
  const { content, resolveImg } = useSiteContent();
  const g = content.general;
  const logo = resolveImg(g.logo);
  const NAV = NAV_ROUTES.map((n) => ({ ...n, label: g[`nav_${n.key}`] || NAV_FALLBACK[n.key] }));
  const [scrolled, setScrolled] = useState(false);
  const isHome = useLocation().pathname === "/";
  const [open, setOpen] = useState(false);
  const portalHref = user ? (user.role === "client" ? "/dashboard" : "/admin") : "/login";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const linkCls = ({ isActive }) =>
    `text-sm font-semibold transition-colors ${isActive ? "text-teal-600" : "text-slate-600 hover:text-teal-600"}`;

  return (
    <div className="relative min-h-screen flex flex-col bg-[#171e25] text-slate-100">
      <LayersHero fixed />
      {isHome && <AnnouncementPopup />}
      <header className={`fixed top-0 inset-x-0 z-50 bg-white/95 border-b transition-shadow ${scrolled ? "border-slate-200 shadow-sm" : "border-transparent"}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-20 flex items-center justify-between">
          <Link to="/" data-testid="public-logo"><img src={logo} alt="Officina Dentale 3D" className="h-14 object-contain" /></Link>
          <nav className="hidden md:flex items-center gap-8">
            {NAV.map((n) => <NavLink key={n.to} to={n.to} end={n.end} className={linkCls} data-testid={`nav-${n.key}`}>{n.label}</NavLink>)}
          </nav>
          <div className="hidden md:block">
            <Link to={portalHref}>
              <Button data-testid="area-clienti-button" className="bg-teal-600 hover:bg-teal-700 rounded-full px-6">{g.area_clienti_label || "Area Clienti"}</Button>
            </Link>
          </div>
          <button className="md:hidden text-slate-700" onClick={() => setOpen(!open)} data-testid="mobile-menu-toggle">
            {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
        {open && (
          <div className="md:hidden bg-white border-t border-slate-200 px-6 py-4 space-y-3">
            {NAV.map((n) => <NavLink key={n.to} to={n.to} end={n.end} onClick={() => setOpen(false)} className="block text-slate-700 font-semibold">{n.label}</NavLink>)}
            <Link to={portalHref}><Button className="w-full bg-teal-600 hover:bg-teal-700 mt-2">{g.area_clienti_label || "Area Clienti"}</Button></Link>
          </div>
        )}
      </header>

      <main className="relative z-10 flex-1">{children}</main>

      <footer className="relative z-10 bg-slate-950/80 text-slate-300">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 grid grid-cols-1 md:grid-cols-4 gap-10">
          <div className="md:col-span-2">
            <img src={logo} alt="Officina Dentale 3D" className="h-16 object-contain mb-4 brightness-0 invert opacity-90" />
            <p className="text-sm leading-relaxed max-w-sm text-slate-400">{g.footer_tagline}</p>
          </div>
          <div>
            <h4 className="font-heading font-semibold text-white mb-4">Naviga</h4>
            <ul className="space-y-2 text-sm">
              {NAV.map((n) => <li key={n.to}><Link to={n.to} className="hover:text-teal-400">{n.label}</Link></li>)}
              <li><Link to={portalHref} className="hover:text-teal-400">Area Clienti</Link></li>
              <li><Link to="/privacy" className="hover:text-teal-400" data-testid="footer-privacy-link">Privacy</Link></li>
            </ul>
          </div>
          <div>
            <h4 className="font-heading font-semibold text-white mb-4">Contatti</h4>
            <ul className="space-y-3 text-sm text-slate-400">
              <li className="flex items-center gap-2"><Phone className="h-4 w-4 text-teal-400" /> {g.phone}</li>
              <li className="flex items-center gap-2"><Mail className="h-4 w-4 text-teal-400" /> {g.email}</li>
              <li className="flex items-center gap-2"><MapPin className="h-4 w-4 text-teal-400" /> {g.address}</li>
            </ul>
          </div>
        </div>
        <div className="border-t border-slate-800 py-6 text-center text-xs text-slate-500">
          © {new Date().getFullYear()} {g.footer_copyright || "Officina Dentale 3D print — Tutti i diritti riservati"}
        </div>
      </footer>
    </div>
  );
}
